<template>
  <div v-if="show" class="chry-fall-overlay">
    <div
      class="chry-container"
      :class="{ 'fade-out': fading }"
      :style="{ transition: `opacity ${props.fadeOutMs}ms ease-out` }">
      <img
        v-for="it in items"
        :key="it.id"
        :src="imgSrc"
        class="chry"
        :style="{
          left: it.left + '%',
          width: it.size + 'px',
          animationDuration: `${it.speed}ms, ${it.speed}ms`,
          animationDelay: `${Math.round(it.delay)}ms, ${Math.round(it.delay)}ms`,
          animationTimingFunction: `${it.fallTiming}, linear`,
          animationIterationCount: '1, 1',
          '--drift-total': it.driftTotal + 'px',
          '--start-top': it.startTopVh + 'vh',
          '--end-top': props.endTopVh + 'vh'
        }"
        alt="" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import imgSrcDefault from '@/assets/pixi/ui/cake_icon.png'

const props = defineProps({
  show: { type: Boolean, default: false },
  // 更稀疏：默认数量减少，可按需传入
  count: { type: Number, default: 12 },
  durationMs: { type: Number, default: 9000 },
  // 保持大小基本相等：提供固定像素大小
  sizePx: { type: Number, default: 36 },
  // 掉落图片资源（可传入），默认使用蛋糕图标
  imgSrc: { type: String, default: imgSrcDefault },
  // 摆动幅度（像素），收敛默认值以减少过大位移
  swayAmpPx: { type: Number, default: 24 },
  // 释放窗口：用于控制延迟分布，增强上下间距
  spawnWindowMs: { type: Number, default: 4000 },
  // 起始高度范围（vh），避免同时从同一高度出现
  startTopVhMin: { type: Number, default: -35 },
  startTopVhMax: { type: Number, default: -8 },
  // 结束下落高度（vh）
  endTopVh: { type: Number, default: 115 },
  // 行数与每行数量（覆盖实际布局）
  rows: { type: Number, default: 5 },
  perRow: { type: Number, default: 2 },
  // 行间横向错落幅度（百分比）
  rowStaggerX: { type: Number, default: 8 },
  // 全部落底后统一淡出时间（毫秒）
  fadeOutMs: { type: Number, default: 600 },
  // 所有落底后停留等待的时间（毫秒）
  holdAfterLandMs: { type: Number, default: 400 }
})

type Item = {
  id: number
  left: number
  size: number
  delay: number
  speed: number
  swayDuration: number
  swayDelay: number
  swayAmp: number
  rotAmp: number
  startTopVh: number
  fallTiming: string
  driftL1: number
  driftR2: number
  driftL3: number
  driftTotal: number
}

// 在模板中使用的图片src，来自可传入的props，具有默认值
const imgSrc = computed(() => props.imgSrc)

const items = ref<Item[]>([])
const fading = ref(false)
let fadeTimer: number | undefined
let clearTimer: number | undefined

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n))
}

// 生成正态分布随机数（Box-Muller），并做裁剪
function randNormal(mean: number, std: number, min?: number, max?: number) {
  let u = 0,
    v = 0
  while (u === 0) u = Math.random()
  while (v === 0) v = Math.random()
  const z = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v)
  let x = mean + std * z
  if (typeof min === 'number' && typeof max === 'number') {
    x = clamp(x, min, max)
  }
  return x
}

function generateItems() {
  const arr: Item[] = []
  const rows = Math.max(1, props.rows)
  const perRow = Math.max(1, props.perRow)
  const count = rows * perRow
  const rowGapMs = rows > 1 ? props.spawnWindowMs / rows : 0

  // 两列，左右间距接近
  const baseXs = [30, 70]

  let id = 0
  const range = props.startTopVhMax - props.startTopVhMin
  const step = rows > 1 ? range / (rows - 1) : 0

  for (let r = 0; r < rows; r++) {
    const rowOffset = r * rowGapMs
    // 相邻上下两层错落（左右相反方向偏移）
    const altSign = r % 2 === 0 ? -1 : 1
    const rowShift = altSign * props.rowStaggerX + (Math.random() - 0.5) * 6

    // 行起始高度均匀且轻微抖动
    const rowStartTopBase = props.startTopVhMin + r * step
    const rowStartTopVh = rowStartTopBase + (Math.random() - 0.5) * 4

    for (let k = 0; k < perRow && id < count; k++, id++) {
      const baseX = baseXs[k % baseXs.length]
      const jitterX = (Math.random() - 0.5) * 12
      const left = clamp(baseX + rowShift + jitterX, 5, 95)

      // 行内小延迟，避免完全对齐
      const delay = clamp(rowOffset + Math.random() * 300, 0, props.spawnWindowMs)

      // 下落时长加入轻微差异，更自然
      const speed = clamp(
        Math.round(props.durationMs * (0.97 + (Math.random() - 0.5) * 0.06)),
        800,
        props.durationMs * 1.08
      )
      // 左右摆动时长略慢，正态分布采样（独立于下落时长）
      const swayDuration = Math.round(randNormal(3200, 400, 2400, 4200))
      const swayDelayOffset = (Math.random() - 0.5) * 400
      const swayDelay = clamp(delay + swayDelayOffset, 0, props.spawnWindowMs)

      const swayAmp = Math.max(2, props.swayAmpPx + (Math.random() - 0.5) * 10)
      // 旋转幅度与水平位移强度弱相关：更自然的轻摆（带符号）
      const rotSign = Math.random() < 0.5 ? -1 : 1
      const rotBase = randNormal(3.2, 0.8, 2.2, 4.2)
      const rotAmp = rotSign * rotBase
      // 为每朵随机一个“慢启动、逐步加速”的下落曲线，增强自然感
      const fallEaseSet = ['cubic-bezier(.32,0,.67,1)', 'cubic-bezier(.25,.05,.5,1)', 'cubic-bezier(.2,.1,.4,1)']
      const fallTiming = fallEaseSet[Math.floor(Math.random() * fallEaseSet.length)]

      // 漂移模式：先左到 l1，再右到 r2，再左到 l3（小幅但自然，正态分布+相关性）
      const l1Mag = randNormal(55, 8, 40, 80) // 左一段的绝对值（更小，40~80，均值约55）
      const driftL1 = -l1Mag
      // 右段与左段相关：约为左段的 1.4~1.8 倍，整体更克制
      const r2Mean = l1Mag * randNormal(1.6, 0.1, 1.4, 1.8)
      const driftR2 = clamp(randNormal(r2Mean, 8, 100, 150), 100, 150)
      // 左三段略小于左一段（约 0.7~0.9 倍），更自然的回摆
      const l3Mean = l1Mag * randNormal(0.8, 0.06, 0.7, 0.9)
      const driftL3 = -clamp(randNormal(l3Mean, 6, 35, 70), 35, 70)

      // 线性横向漂移：更大随机总位移，随下落全程线性叠加（保持速度）
      const driftSign = Math.random() < 0.5 ? -1 : 1
      const driftTotal = driftSign * clamp(randNormal(120, 40, 60, 200), 60, 200)

      arr.push({
        id,
        left,
        // 尺寸轻微随机，增强自然感
        size: clamp(props.sizePx + (Math.random() - 0.5) * 4, props.sizePx - 4, props.sizePx + 4),
        delay,
        speed,
        swayDuration,
        swayDelay,
        swayAmp,
        rotAmp,
        startTopVh: rowStartTopVh,
        fallTiming,
        driftL1,
        driftR2,
        driftL3,
        driftTotal
      })
    }
  }
  items.value = arr
}

watch(
  () => props.show,
  (val) => {
    console.log('🌸 PatternFallOverlay show 状态变化:', val)
    if (val) {
      console.log('🌸 开始生成菊花元素，数量:', props.count)
      generateItems()
      // 计算最后一个完成时间：delay + speed
      const maxFinishMs = items.value.reduce((max, it) => Math.max(max, Math.round(it.delay + it.speed)), 0)
      // 等全部落底后等待一会再统一淡出
      if (fadeTimer) clearTimeout(fadeTimer)
      if (clearTimer) clearTimeout(clearTimer)
      fadeTimer = window.setTimeout(() => {
        fading.value = true
      }, maxFinishMs + props.holdAfterLandMs)
      clearTimer = window.setTimeout(
        () => {
          items.value = []
          fading.value = false
        },
        maxFinishMs + props.holdAfterLandMs + props.fadeOutMs
      )
    } else {
      console.log('🌸 隐藏组件，清空元素')
      if (fadeTimer) clearTimeout(fadeTimer)
      if (clearTimer) clearTimeout(clearTimer)
      fading.value = false
      items.value = []
    }
  }
)
</script>

<style scoped>
.chry-fall-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  pointer-events: none; /* 不拦截用户交互 */
}

.chry-container {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.chry-container.fade-out {
  opacity: 0;
}

.chry {
  position: absolute;
  top: var(--start-top);
  will-change: top, transform, opacity;
  animation-name: none, none;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
  opacity: 0.95;
}

@keyframes chry-fall {
  0% {
    top: var(--start-top);
    opacity: 0;
  }
  5% {
    opacity: 0.95;
  }
  100% {
    top: var(--end-top);
    opacity: 1;
  }
}

/* 线性横向漂移：从 0 到总位移，线性速度 */
@keyframes chry-drift {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(var(--drift-total));
  }
}

/* 移除左右摇摆，仅保留下落 */
</style>
